import { Field } from "formik";
import React, { FC, useState, useEffect } from "react";
import { StyleFormWrapper, StyleLabel } from "../style";
import ErrorMessage from "../ErrorMessage";
import styled from "@emotion/styled";

// Reusing the orange color palette if needed, or defining simple colors
const orange = "#ff3135";

interface InputProps {
  name: string;
  placeholder?: string;
  label?: string;
}

const Wrapper = styled.div<{ hasError?: boolean }>`
  display: flex;
  align-items: center;
  border: 1px solid ${(props) => (props.hasError ? "red" : "#e2e8f0")};
  border-radius: 12px;
  background-color: #f8fafc;
  padding: 4px;
  transition: all 0.2s;
  &:focus-within {
    border-color: ${orange};
    background-color: #fff;
    box-shadow: 0 0 0 3px rgba(255, 49, 53, 0.1);
  }
`;

const InputGroup = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const StyledNumberInput = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  padding: 8px;
  font-size: 14px;
  text-align: center;
  color: #334155;
  font-weight: 500;
  outline: none;
  &::placeholder {
    color: #cbd5e1;
  }
  appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const Separator = styled.span`
  color: #94a3b8;
  font-weight: 600;
  padding: 0 4px;
`;

const StyledSelect = styled.select`
  border: none;
  background: transparent;
  font-size: 14px;
  color: #334155;
  font-weight: 600;
  outline: none;
  cursor: pointer;
  text-align: right;
  margin-left: auto;
  appearance: none;
  /* Custom arrow for cleaner look */
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 1.5rem;
`;

const DeliveryTime: FC<InputProps> = ({ name, label }) => {
  return (
    <Field name={name}>
      {({ field, meta, form }: any) => {
        const hasError = meta.touched && meta.error;
        const initialValue = field.value || "";

        // Parse initial value
        const parseValue = (val: string) => {
          if (!val) return { min: "", max: "", unit: "min" };
          const match = val.match(/^(\d+)\s*-\s*(\d+)\s*(\w+)/i);
          if (!match) return { min: "", max: "", unit: "min" };

          let unitRaw = (match[3] || "min").toLowerCase();
          // Normalize unit to singular form for select value
          if (unitRaw.startsWith("hour")) unitRaw = "hour";
          else if (unitRaw.startsWith("day")) unitRaw = "day";
          else unitRaw = "min";

          return {
            min: match[1],
            max: match[2],
            unit: unitRaw,
          };
        };

        const [state, setState] = useState(() => parseValue(initialValue));

        // Sync local state if external value changes significantly (e.g. form reset)
        useEffect(() => {
          const parsed = parseValue(field.value);
          if (!field.value && (state.min || state.max)) {
            setState({ min: "", max: "", unit: "min" });
          } else if (field.value) {
            // If the field has a value, verify if our state generally matches.
            // We avoid deep matching to prevent cursor jumps, but we ensure we aren't desynced.
            const currentStr = `${state.min}-${state.max} ${state.unit === "min" ? "mins" : state.unit === "hour" ? "hours" : "days"}`;
            // If completely different (e.g. loaded from server), sync.
            // This is a simple check; for perfect sync we might compare parts.
            if (parsed.min !== state.min && state.min === "") {
              setState(parsed);
            }
          }
        }, [field.value]);

        const updateForm = (newState: typeof state) => {
          const { min, max, unit } = newState;
          if (min && max) {
            // Pluralize for display/storage standard
            const unitLabel = parseInt(max) === 1 ? unit : `${unit}s`;
            const val = `${min}-${max} ${unitLabel}`;
            form.setFieldValue(name, val);
          } else if (!min && !max) {
            form.setFieldValue(name, "");
          }
        };

        const handleChange = (key: keyof typeof state, value: string) => {
          const newState = { ...state, [key]: value };
          setState(newState);
          updateForm(newState);
        };

        return (
          <StyleFormWrapper>
            {!!label && <StyleLabel hasError={hasError}>{label}</StyleLabel>}
            <Wrapper hasError={hasError}>
              <InputGroup>
                <StyledNumberInput
                  placeholder="Min"
                  type="number"
                  min="0"
                  value={state.min}
                  onChange={(e) => handleChange("min", e.target.value)}
                />
                <Separator>-</Separator>
                <StyledNumberInput
                  placeholder="Max"
                  type="number"
                  min="0"
                  value={state.max}
                  onChange={(e) => handleChange("max", e.target.value)}
                />
              </InputGroup>
              <div
                style={{
                  borderLeft: "1px solid #e2e8f0",
                  height: "24px",
                  margin: "0 8px",
                }}
              />
              <StyledSelect
                value={state.unit}
                onChange={(e) => handleChange("unit", e.target.value)}
              >
                <option value="min">Mins</option>
                <option value="hour">Hours</option>
                <option value="day">Days</option>
              </StyledSelect>
            </Wrapper>
            {!!hasError && <ErrorMessage message={meta.error} />}
          </StyleFormWrapper>
        );
      }}
    </Field>
  );
};

export default DeliveryTime;
